/**
 * Tuning knobs in one place. Bot balance runs (tools/bots) check these against the pacing targets;
 * change them here, rerun `pnpm bots`, and record the reason in docs/DECISIONS.md.
 */
export const BALANCE = {
  /** Audience sizes in content are relative weekly buyers; this turns them into copies. */
  salesScale: 2.0,
  /** Competition: sales divide by 1 + (games for this audience in the last 2 years) / crowding. */
  crowding: 8,
  /** Interest from review score: (score/10)^interestPower * interestMult. */
  interestPower: 2.0,
  interestMult: 1.0,
  /** Focus match: M = similarity * (focusBase + focusExec * design quality). */
  focusBase: 0.45,
  focusExec: 0.55,
  /** Projects a studio can run at once. */
  maxProjects: { garage: 1, office: 2, studio: 3, hq: 4 } as Record<string, number>,
  /** Bigger teams help less per head (team^teamExp / team). */
  teamExp: 0.65,
  /** Games get bigger over the decades: work need grows by this much per year since 1965. */
  workPerYear: 0.05,
  /** Overall size of a project: a standard game takes a small team about a season. */
  workScale: 2.6,
  /** Your first games are small: projects started in the first seven years need this share of the work, rising to all of it. */
  firstYearsWork: 0.3,
  /** Rivals take this much longer between releases than their content cadence (games got bigger for everyone). */
  rivalPace: 1.3,
  /** Shops stock only so many new games a year from one publisher; past that each one gets less shelf space. */
  shelfSlots: { garage: 2, office: 3, studio: 3, hq: 4 } as Record<string, number>,
  shelfPenalty: 0.6,
  /** Past the shelf limit, shops charge to list another new game (1965 dollars, per game over). */
  listingFee: 2000,
  /** ...or this share of what the game should earn in its first year, if more. */
  listingShare: 0.08,
  /** Outside designers: the smallest advance anyone asks (1965 dollars). */
  pitchBase: 3000,
  /** Pre-orders: each week of art and rules, this share of the game's expected first-year sales, times its hype, order ahead. */
  preorderRate: 0.006,
  /** Crowdfunding: over 4 weeks, backers = expected first-year sales × (base + hype × perHype); ship within crowdDeadline weeks. */
  crowdBase: 0.06,
  crowdPerHype: 0.12,
  crowdDeadline: 30,
  /** the platform's cut of what backers pledge; and the reputation lost by cancelling a funded game */
  crowdFee: 0.07,
  crowdCancelRep: 10,
  /** Events scale up for companies earning more than this a year (1965 dollars). */
  eventScaleIncome: 60000,
  /** How much a hot theme lifts sales (× its heat, 0..1). */
  trendBoost: 0.8,
  /** R&D budget: the most you can put in (share of weekly sales), and what a research point costs (1965 dollars, before era costs). */
  rdMax: 0.2,
  rdPointCost: 900,
  /** Rival moves against you: at least this many weeks apart. A lawyer's letter costs this (1965 dollars). */
  rivalMoveGapWeeks: 40,
  lawyerFee: 2500,
  /** Outbidding a rival for a licence raises its fee by this much. */
  licenceRaise: 1.3,
  /** Buyouts: at most this many publishers owned, one every this many years. */
  maxBuyouts: 4,
  buyoutGapYears: 5,
  /** The holiday shelf (weeks 44–50) sells more; January sells less. */
  holidayBoost: 1.3,
  januaryDip: 0.9,
  /** Running a bigger company costs more than salaries: managers, insurance, IT. Share of payroll per person, capped. */
  overheadPerPerson: 0.006,
  overheadCap: 0.45,
  /** Tax on yearly profit above taxFree (1965 dollars), paid each January. */
  taxRate: 0.3,
  taxFree: 120000,
  /** Profit above taxHigh (1965 dollars) pays the higher rate. */
  taxHigh: 1000000,
  taxHighRate: 0.5,
  /** Research runs this many items at once, by studio size. */
  researchSlots: { garage: 1, office: 1, studio: 2, hq: 3 } as Record<string, number>,
  /** Research gets dearer each era: cost × eraCost^(era number). */
  eraCost: 1.8,
  /** Market reception: each launch draws a hidden multiplier exp(N(0, receptionSd)) the forecast cannot see. */
  receptionSd: 0.45,
  /** Playtest polish: P = 1 - problemWeight * (sum of problem severities left). */
  problemWeight: 1.2,
  /** Your own recent releases for the same audience split its attention: / (1 + ownCannibal * n in the last 26 weeks). */
  ownCannibal: 0.12,
  /** Perceived value of components: partsValue * (sum of part values * taste)^partsCurve. */
  partsValue: 1.25,
  partsCurve: 0.8,
  componentTaste: { kids: 0.65, family: 0.75, party: 0.6, hobby: 1.0, hardcore: 1.0, collectors: 1.3, roleplayers: 0.8 } as Record<string, number>,
  /** Absolute price sensitivity: sales *= (budgetShare * audience ceiling / price)^priceElasticity. */
  budgetShare: 0.6,
  priceElasticity: 0.6,
  /** Outside storage, per pallet per week (1965 dollars). */
  palletWeek: 3,
  /** Evergreen floor: weekly copies = A * R * pi * crowding * evergreenShare * ... */
  evergreenShare: 0.03,
  /** Evergreen sales fade with this e-folding time (weeks). */
  evergreenFade: 480,
  /** Your own evergreens in the same audience share shelf space: floor / (1 + cannibal * (n - 1)). */
  evergreenCannibal: 0.25,
  /** True replay intent weights. */
  replay: { base: 0.23, fit: 0.16, focus: 0.12, slider: 0.035, asym: 0.08, mastery: 0.08, quality: 0.2, problems: 0.18 },
  /** Imprints (bought rivals): their new releases pay this share of the buyout's catalogue royalty, every week, for good. */
  imprintShare: 0.15,
  /** An imprint's focus: an audience it already serves, another audience, or a theme (riskier: rides the theme's heat). */
  imprintFocus: { audienceMatch: 1.2, audienceOther: 1.05, audienceClosed: 0.9, themeBase: 0.85, themeHeat: 0.6, themeMatch: 0.1 },
  /** An imprint can change its focus once a year. */
  imprintRefocusWeeks: 52,
  /** Booths: fans (in 1965's market, grows with it), a sales lift for a few weeks, hype for a game still in the works. */
  boothFans: 600,
  boothSales: 0.25,
  boothWeeks: 8,
  boothHype: 0.12,
  /** At an awards convention, a booth for a game from the last year may catch the judges' eye. */
  boothNominateP: 0.3,
  boothNominateBoost: 0.3,
  /** Fan mail: weeks between letters (fewer with a bigger fan base), how often a letter asks for something, its fans. */
  fanmailGap: [14, 30] as [number, number],
  fanmailAskP: 0.35,
  fanmailThanksFans: 100,
  fanmailPromiseFans: 800,
};
